import React, { createContext, Dispatch, SetStateAction, useState, useCallback, useContext, useRef } from "react";
import { HighlightArea, highlightPlugin, RenderHighlightsProps, HighlightPlugin } from "@react-pdf-viewer/highlight";

export type ICitationHighlightItemType = "CASE_OVERVIEW" | "CASEY" | "EVALUATION";

export interface ICitationHighlightItem {
  type: ICitationHighlightItemType;
  externalId: string;
  attachmentId: string;
  highlightArea: HighlightArea;
  spanText?: string;
}

interface ReviewAIHighlightProviderProps {
  children: React.ReactNode;
}

export interface ReviewAIHighlightContextType {
  setAttachmentId: Dispatch<SetStateAction<string | undefined>>;
  reviewAIHighlightPluginInstance: HighlightPlugin | undefined;
  hightlightCitationItems: (items: ICitationHighlightItem[]) => void;
  selectedCitationId: string | undefined;
  setSelectedCitationId: Dispatch<SetStateAction<string | undefined>>;
  hoveredChatbotCitationId: string | undefined;
  setHoveredChatbotCitationId: Dispatch<SetStateAction<string | undefined>>;
  onReviewAIDocumentLoad: (attachmentId: string | undefined) => void;
}

const defaultState: ReviewAIHighlightContextType = {
  setAttachmentId: () => {},
  reviewAIHighlightPluginInstance: undefined,
  hightlightCitationItems: () => {},
  selectedCitationId: undefined,
  setSelectedCitationId: () => {},
  hoveredChatbotCitationId: undefined,
  setHoveredChatbotCitationId: () => {},
  onReviewAIDocumentLoad: () => {},
};

export const ReviewAIHighlightContext = createContext<ReviewAIHighlightContextType>(defaultState);

export const ReviewAIHighlightProvider = ({ children }: ReviewAIHighlightProviderProps) => {
  const [highlightedCitationItems, setHighlightedCitationItems] = useState<ICitationHighlightItem[]>([]);

  const highlightedCitationItemsRef = useRef<ICitationHighlightItem[]>([]);

  const [attachmentId, setAttachmentId] = useState<string | undefined>();

  const [selectedCitationId, setSelectedCitationId] = useState<string | undefined>(undefined);
  const [hoveredChatbotCitationId, setHoveredChatbotCitationId] = useState<string | undefined>(undefined);

  const highlightedAttachmentId = useRef<string | undefined>(undefined);
  const highlightedArea = useRef<HighlightArea | undefined>(undefined);
  const alreadyJumped = useRef<boolean>(false);

  const getHighlights = useCallback(() => {
    return highlightedCitationItems.filter((highlight) => attachmentId && highlight.attachmentId === attachmentId);
  }, [highlightedCitationItems, attachmentId]);

  const renderHighlights = (props: RenderHighlightsProps) => (
    <div>
      {getHighlights()
        .filter((highlight) => highlight.highlightArea.pageIndex === props.pageIndex)
        .map((highlight, idx) => (
          <React.Fragment key={`${highlight.attachmentId}-${idx}`}>
            <div
              key={idx}
              className="highlight"
              style={Object.assign(
                {},
                {
                  mixBlendMode: "multiply",
                  zIndex: 0,
                  border: "2px solid #039EC3",
                  borderRadius: "4px",
                },
                props.getCssProperties(highlight.highlightArea, props.rotation)
              )}
            />
          </React.Fragment>
        ))}
    </div>
  );

  const reviewAIHighlightPluginInstance = highlightPlugin({
    renderHighlights,
  });

  const { jumpToHighlightArea } = reviewAIHighlightPluginInstance;

  function hightlightCitationItems(items: ICitationHighlightItem[]) {
    highlightedCitationItemsRef.current = items;

    if (items.length === 0) {
      setHighlightedCitationItems([]);
      return;
    }

    const firstItem = items[0];
    const firstHighlightArea: HighlightArea = firstItem.highlightArea;

    const itemsAttachmentId = firstItem.attachmentId;

    // This is to give some space and not scroll exactly to the top value of the first highlight
    const eligibleTopValuesToJumpTo: number[] = [
      firstHighlightArea.top - 2,
      firstHighlightArea.top - 5,
      firstHighlightArea.top - 10,
      firstHighlightArea.top - 20,
    ].filter((value) => value >= 0);

    const topValueToJumpTo: number = eligibleTopValuesToJumpTo.length > 0 ? Math.min(...eligibleTopValuesToJumpTo) : 0;

    const hightlightAreaToJumpTo: HighlightArea = {
      ...firstHighlightArea,
      top: topValueToJumpTo,
    };

    if (itemsAttachmentId === attachmentId) {
      setHighlightedCitationItems(items);
      jumpToHighlightArea(hightlightAreaToJumpTo);
      alreadyJumped.current = true;
    } else {
      alreadyJumped.current = false;
    }

    highlightedAttachmentId.current = itemsAttachmentId;
    highlightedArea.current = hightlightAreaToJumpTo;
  }

  const onReviewAIDocumentLoad = useCallback(
    (attachmentId: string | undefined) => {
      if (attachmentId === undefined) {
        return;
      }
      if (alreadyJumped.current) {
        return;
      }

      // Even after the document load callback is called, the rendered pdf's state is still not yet ready for scroll.
      // So it fails trying to change the page.
      // As a workaround, we delay the scroll by 500ms, and ONLY when a different attachment is displayed.
      setTimeout(() => {
        if (attachmentId === highlightedAttachmentId.current && highlightedArea.current) {
          setHighlightedCitationItems(highlightedCitationItemsRef.current);
          jumpToHighlightArea(highlightedArea.current);
          alreadyJumped.current = true;
        }
      }, 500);
    },
    [jumpToHighlightArea]
  );

  return (
    <ReviewAIHighlightContext.Provider
      value={{
        setAttachmentId,
        reviewAIHighlightPluginInstance,
        hightlightCitationItems,
        selectedCitationId,
        setSelectedCitationId,
        hoveredChatbotCitationId,
        setHoveredChatbotCitationId,
        onReviewAIDocumentLoad,
      }}
    >
      {children}
    </ReviewAIHighlightContext.Provider>
  );
};

export const useReviewAIHighlightContext = () => {
  const context = useContext(ReviewAIHighlightContext);
  if (!context) {
    return defaultState;
  }
  return context;
};
